import { API_LIMIT_TYPES_BY_CELL_TYPE, BrokenLimitCellType, BrokenLimitFilterValue, shouldPaintBrokenLimit } from '../utils/brokenLimitWarningUtils';
import { isOutsidePercentageLimit } from './limitInfoUtils';

export type LimitSkin = 'gray' | 'red' | 'amber';

export type LimitConformityInput = {
  adjustedPriceLimitTypes?: string[] | null;
  priceStatus?: string | null;
  price?: number | null;
  suggestedPrice?: number | null;
  retailPrice?: number | null;
  novaMargem?: number | null;
  minMargin?: number | null;
  maxMargin?: number | null;
  novaCompetitividade?: number | null;
  competitorsPrice?: number | null;
  minCpi?: number | null;
  maxCpi?: number | null;
};

const CONFORMABLE_PRICE_STATUSES: readonly string[] = ['ERP', 'AUTO', 'OTIMIZADO'];

/**
 * IT-3358 / RN01: o ambar indica que o motor ajustou o preco pelo limite. Vale para os status que o motor
 * produz (ERP, AUTO, OTIMIZADO); um preco editado nao foi conformado, entao o destaque cai.
 */
const isConformedByLimit = (rowData: LimitConformityInput, type: BrokenLimitCellType): boolean => {
  const { adjustedPriceLimitTypes, priceStatus } = rowData;

  if (!Array.isArray(adjustedPriceLimitTypes)) return false;
  if (!priceStatus || !CONFORMABLE_PRICE_STATUSES.includes(priceStatus)) return false;

  return API_LIMIT_TYPES_BY_CELL_TYPE[type].some(apiType => adjustedPriceLimitTypes.includes(apiType));
};

export const isMarginOutsideLimit = ({ novaMargem, minMargin, maxMargin }: LimitConformityInput): boolean =>
  isOutsidePercentageLimit(novaMargem, minMargin, maxMargin);

export const isCpiOutsideLimit = ({ novaCompetitividade, competitorsPrice, maxCpi, minCpi }: LimitConformityInput): boolean =>
  !!competitorsPrice &&
  competitorsPrice > 0 &&
  !!novaCompetitividade &&
  !!maxCpi &&
  !!minCpi &&
  (novaCompetitividade > maxCpi * 100 || novaCompetitividade < minCpi * 100);

type ResolveLimitSkinParams = {
  rowData: LimitConformityInput;
  type: BrokenLimitCellType;
  localBroken: boolean;
  brokenLimitFilter?: BrokenLimitFilterValue;
};

// O ambar da conformacao (IT-3358) tem precedencia sobre o vermelho (RN02). O vermelho e o da celula:
// a quebra local que ela calcula, pintada conforme o filtro de limites quebrados (IT-4581).
export const resolveLimitSkin = ({ rowData, type, localBroken, brokenLimitFilter = null }: ResolveLimitSkinParams): LimitSkin => {
  if (isConformedByLimit(rowData, type)) return 'amber';

  return shouldPaintBrokenLimit({ rowData, localBroken, brokenLimitFilter }) ? 'red' : 'gray';
};

export const resolveMarginLimitSkin = (rowData: LimitConformityInput, brokenLimitFilter: BrokenLimitFilterValue = null): LimitSkin =>
  resolveLimitSkin({ rowData, type: 'MARGIN', localBroken: isMarginOutsideLimit(rowData), brokenLimitFilter });

export const resolveCpiLimitSkin = (rowData: LimitConformityInput, brokenLimitFilter: BrokenLimitFilterValue = null): LimitSkin =>
  resolveLimitSkin({ rowData, type: 'COMPETITIVENESS', localBroken: isCpiOutsideLimit(rowData), brokenLimitFilter });
